const MICROSOFT_AUTHORITY_HOSTS = new Set([
  'login.microsoftonline.com',
  'login.microsoft.com',
  'login.microsoftonline.us',
  'login.chinacloudapi.cn',
  'login.partner.microsoftonline.cn',
]);

const isMicrosoftAuthority = (url: string): boolean => {
  const match = url.match(/^https?:\/\/([^/?#:]+)/i);

  if (!match) {
    return false;
  }

  return MICROSOFT_AUTHORITY_HOSTS.has(match[1].toLowerCase());
};

/**
 * Derive the OAuth2 authorization URL from the token URL.
 *
 * Handles Microsoft, Google, and generic OAuth2 providers.
 */
export const deriveAuthorizationUrl = (tokenUrl: string): string | null => {
  if (!tokenUrl) {
    return null;
  }

  // Microsoft: replace /token with /authorize
  if (isMicrosoftAuthority(tokenUrl)) {
    return tokenUrl.replace(/\/token\/?$/, '/authorize');
  }

  // Google: map token endpoint to authorization endpoint
  if (tokenUrl.includes('oauth2.googleapis.com/token')) {
    return 'https://accounts.google.com/o/oauth2/v2/auth';
  }

  // Generic fallback: replace last "token" segment with "authorize"
  return tokenUrl.replace(/\/token\/?$/, '/authorize');
};

/**
 * Build a full OAuth2 authorization URL with query parameters.
 */
export const buildAuthorizationUrl = (
  authEndpoint: string,
  clientId: string,
  scope: string,
  redirectUri: string
): string => {
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
  });

  if (isMicrosoftAuthority(authEndpoint)) {
    const scopes = scope.split(/\s+/).filter(Boolean);

    params.set('prompt', 'select_account');

    if (scopes.length > 0) {
      if (!scopes.includes('offline_access')) {
        scopes.push('offline_access');
      }

      params.set('scope', scopes.join(' '));
    }
  } else {
    params.set('access_type', 'offline');
    params.set('prompt', 'consent');

    if (scope) {
      params.set('scope', scope);
    }
  }

  return `${authEndpoint}?${params.toString()}`;
};
